import React, { useState, useEffect } from "react";
import { apiService } from "@services/api";
import { CONTACT_INFO } from "@constants";
import {
  Button,
  Grid,
  Tooltip,
  Typography,
  Box,
  styled,
  Container,
  CardContent,
  Card,
} from "@mui/material";
import PropertyFilters from "./Propertyfilter";
import { CardMedia, Divider } from "@mui/material";
import {
  MdFamilyRestroom,
  MdOutlineWorkspacePremium,
  MdSecurity,
  MdSupportAgent,
  MdVerifiedUser,
  MdRealEstateAgent,
} from "react-icons/md";
import { FaLocationDot, FaCalendar } from "react-icons/fa6";

import ContactSection from "./contactSection";
import { SectionHeader } from "./style";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import whydubai from "@assets/images/Why-Dubai.jpg";
import freehold from "@assets/images/FreeHold-in-Dubai.jpg";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import {
  FaBuilding,
  FaMapMarkedAlt,
  FaChartLine,
  FaGem,
  FaLock,
  FaLandmark,
} from "react-icons/fa";
import contactBg from "@assets/images/contactbg.jpg";
import NewsletterSignup from "./NewsLetter";
import Links from "./Links";
import backupBanner from "@assets/images/Real Estate Banner-02.jpg";
import SEO from "../../../utils/SeoComponent";
import { ROUTES } from "../../../constants/routes";

const bannerImages = [
  "../../Images/default-banner.jpg", // Replace with your actual default image path
];

// Styled Components
const PageSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(10, 0),
  position: "relative",
  overflow: "hidden",
}));

const BrandMotionSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(4, 0),
  backgroundColor: "#f9f9f9",
  borderTop: "1px solid #eaeaea",
  borderBottom: "1px solid #eaeaea",
  overflow: "hidden",
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(4),
}));

const WhyDubaiSection = styled(PageSection)(({ theme }) => ({
  backgroundColor: "#ffffff",
}));

const AddonDataHead = styled(Typography)(({ theme }) => ({
  fontSize: "2.5rem",
  fontWeight: 700,
  marginBottom: theme.spacing(2),
  color: "#C10E14",
  position: "relative",
  "&:after": {
    content: '""',
    position: "absolute",
    bottom: "-5px",
    left: "50%",
    transform: "translateX(-50%)",
    width: "80px",
    height: "4px",
    backgroundColor: "#C10E14",
    transition: "all 0.3s ease-in-out",
    [theme.breakpoints.up("md")]: {
      left: 0,
      transform: "none",
    },
  },
}));

const AddonDataContents = styled(Typography)(({ theme }) => ({
  fontSize: "1.2rem",
  fontWeight: 500,
  marginBottom: theme.spacing(2),
  display: "flex",
  alignItems: "start",
  [theme.breakpoints.down("md")]: {
    fontSize: "1rem", // 16px on small screens
  },
}));

const PropertyCard = styled(Card)(({ theme }) => ({
  width: "100%",
  borderRadius: "12px",
  overflow: "hidden",
  transition: "transform 0.3s, box-shadow 0.3s",
  position: "relative",
  "&:hover": {
    transform: "translateY(-5px)",
    boxShadow: theme.shadows[10],
    "& .hoverActions": {
      opacity: 1,
    },
  },
}));

const ImageContainer = styled(Box)({
  position: "relative",
  overflow: "hidden",
  height: 220,
});

const PriceTag = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: 20,
  left: 0,
  backgroundColor: "#C10E14",
  color: "white",
  padding: "6px 15px",
  borderTopRightRadius: "4px",
  borderBottomRightRadius: "4px",
  fontWeight: "bold",
  boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
  zIndex: 2,
}));

const ContactButton = styled(Button)(({ theme }) => ({
  textTransform: "none",
  padding: "8px 16px",
  fontWeight: "bold",
  borderRadius: "8px",
  boxShadow: "none",
  "&:hover": {
    boxShadow: theme.shadows[2],
  },
}));

const Home = () => {
  const navigate = useNavigate();
  const [currentBanner, setCurrentBanner] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [topAreas, setTopAreas] = useState([]);
  const [banners, setBanners] = useState([]);
  const [featuredProperties, setFeaturedProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [developers, setDevelopers] = useState([]);
  const [latestProperties, setLatestProperties] = useState([]);

  useEffect(() => {
    const fetchTopAreas = async () => {
      try {
        const response = await apiService.getAreas();
        setTopAreas(response.data);
      } catch (error) {
        console.error("Error fetching top areas:", error);
      }
    };

    const fetchFeaturedProperties = async () => {
      try {
        const response = await apiService.getFeaturedProperties();
        setFeaturedProperties(response.data.data);
      } catch (error) {
        console.error("Error fetching featured properties:", error);
      }
    };

    const fetchBanners = async () => {
      try {
        setLoading(true);
        const response = await apiService.getBanners();
        if (
          response.data &&
          response.data.data &&
          response.data.data.length > 0
        ) {
          setBanners(response.data);
          setCurrentBanner(0); // Reset to first banner when new data arrives
        } else {
          console.warn("No banner data available or empty array received");
        }
      } catch (error) {
        console.error("Error fetching banner data:", error);
      } finally {
        setLoading(false);
      }
    };

    const fetchDevelopers = async () => {
      try {
        const response = await apiService.getDevelopers();
        setDevelopers(response.data.data);
      } catch (error) {
        console.error("Error fetching developers:", error);
      }
    };

    fetchDevelopers();
    fetchFeaturedProperties();
    fetchTopAreas();
    fetchBanners();
  }, []); // Only runs once

  // Banner slideshow effect
  useEffect(() => {
    // Only set up the interval if we have banner data
    if (banners?.data?.length > 0) {
      const interval = setInterval(() => {
        if (!isHovered) {
          setCurrentBanner((prev) =>
            prev === banners.data.length - 1 ? 0 : prev + 1
          );
        }
      }, 3000); // 3 seconds slide interval

      return () => clearInterval(interval); // Cleanup on unmount
    }
  }, [banners, isHovered]); // Dependencies to prevent stale closures

  useEffect(() => {
    const fetchLatestUpdatedProperties = async () => {
      try {
        const response = await apiService.getLatestProperties();
        setLatestProperties(response.data.data);
      } catch (err) {
        console.error("Error fetching properties:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchLatestUpdatedProperties();
  }, []);

  const whatsappLink = apiService.getWhatsAppLink();

  return (
    <>
      <SEO slug={"home"} />

      <Box className="home-page">
        {/* Hero Section with Banner Slideshow */}
        <Box
          sx={{
            position: "relative",
            overflow: "hidden",
            height: "650px",
            "&::before": {
              content: '""',
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              height: "150px",
              background:
                "linear-gradient(to top, rgba(255, 255, 255, 2), rgba(241, 235, 235, 0))",
              zIndex: 1, // optional: ensures it sits behind overlayed content
            },
          }}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {loading ? (
            <Box
              sx={{
                width: "100%",
                height: "650px", // or "100vh" depending on layout
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                backgroundImage: `url('${backupBanner}')`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
              }}
            >
              {/* Optional: Loading spinner or message */}
            </Box>
          ) : banners?.data && banners.data.length > 0 ? (
            <AnimatePresence initial={false}>
              <motion.img
                key={currentBanner}
                src={banners.data[currentBanner]?.thumbnail}
                alt={`Banner ${currentBanner + 1}`}
                initial={{ opacity: 0, scale: 1.1 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 1.2, ease: "easeInOut" }}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  position: "absolute",
                }}
                onError={(e) => {
                  console.error("Error loading banner image:", e);
                  e.target.src = bannerImages[0]; // Fallback to a default image
                }}
              />
            </AnimatePresence>
          ) : (
            // Fallback to static banner if no data
            <img
              src={backupBanner}
              alt="Default Banner"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
          )}

          {/* Slide indicators - only show if we have banners */}
          {banners?.data && banners.data.length > 0 && (
            <Box
              sx={{
                position: "absolute",
                bottom: "20px",
                left: "50%",
                transform: "translateX(-50%)",
                display: "flex",
                gap: "10px",
                zIndex: 10,
              }}
            >
              {banners.data.map((item, index) => (
                <Box
                  key={index}
                  onClick={() => setCurrentBanner(index)}
                  sx={{
                    width: "12px",
                    height: "12px",
                    borderRadius: "50%",
                    backgroundColor:
                      currentBanner === index
                        ? "white"
                        : "rgba(255,255,255,0.5)",
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                  }}
                />
              ))}
            </Box>
          )}

          {/* Hero Content */}
          <Box
            sx={{
              position: "absolute",
              top: "60%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 5,
              textAlign: "center",
            }}
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.8 }}
            >
              <Typography
                variant="h2"
                component="h1"
                gutterBottom
                fontWeight={700}
                sx={{
                  color: "white",
                  textShadow: "2px 2px 6px rgba(0,0,0,0.5)", // <-- this adds the shadow
                }}
              >
                Find Your Dream Home Now
              </Typography>
            </motion.div>

            {/* Property Filter Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.5, duration: 0.8 }}
              style={{
                width: "90%",
                maxWidth: "1200px",
              }}
            >
              <PropertyFilters />
            </motion.div>
          </Box>

          {/* Gradient Overlay */}
          <Box
            sx={{
              position: "absolute",
              bottom: 0,
              left: 0,
              width: "100%",
              height: "200px",
              background:
                "linear-gradient(to top, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0) 100%)",
              zIndex: 2,
            }}
          />
        </Box>

        {/* Brand Logos Carousel */}
        <BrandMotionSection>
          <div
            style={{
              overflowX: "auto", // enable manual scroll
              whiteSpace: "nowrap",
              paddingBottom: "10px",
              scrollbarWidth: "none", // Firefox hide scrollbar
              msOverflowStyle: "none", // IE/Edge hide scrollbar
            }}
          >
            <motion.div
              style={{
                display: "inline-flex",
                gap: "40px",
                minWidth: "fit-content",
              }}
              animate={{ x: ["0%", "-50%"] }}
              transition={{
                ease: "linear",
                repeat: Infinity,
                duration: 20,
              }}
            >
              {Array.isArray(developers) &&
                [...developers, ...developers].map((brand, index) => (
                  <img
                    key={index}
                    src={brand.image}
                    alt="Brand Logo"
                    style={{
                      cursor: "pointer",
                      maxWidth: "120px",
                      maxHeight: "60px",
                      objectFit: "contain",
                      flexShrink: 0,
                    }}
                    onClick={() =>
                      navigate(ROUTES.DEVELOPER_DETAIL(brand?.slug))
                    }
                  />
                ))}
            </motion.div>
          </div>
        </BrandMotionSection>

        {/* Why Dubai Content Section */}

        <WhyDubaiSection
          id="why-dubai-section"
          sx={{
            bgcolor: "#f6f6f6",
            alignItems: "center",
            display: "flex",
            justifyContent: "center",
            py: { xs: 0, md: 5 }, // Add this to remove top padding
          }}
        >
          <Grid
            container
            sx={{
              mt: { xs: 0, md: 0 },
              px: { xs: 2, sm: 4, md: 6, lg: 8 },
              py: { xs: 2, md: 0 },
              maxWidth: "1400px",
              mx: "auto",
            }}
          >
            {/* Left Text Section */}
            <Grid
              item
              xs={12}
              md={6}
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: { xs: "center", md: "flex-start" },
                py: { xs: 2, md: 4 },
                pl: { xs: 0, md: 4 },
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  maxWidth: "700px",
                  px: { xs: 2, md: 0 },
                }}
              >
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, ease: "easeOut" }}
                  viewport={{ once: true, amount: 0.5 }} // triggers when 50% visible, only once
                >
                  <Box
                    sx={{
                      textAlign: { xs: "center", md: "left" },
                    }}
                  >
                    <AddonDataHead>Why Dubai?</AddonDataHead>
                  </Box>
                </motion.div>

                <AddonDataContents>
                  <FaBuilding
                    size={22}
                    style={{ marginRight: "10px", color: "#C10E14" }}
                  />
                  Unparalleled real estate opportunities with high returns and
                  long-term growth.
                </AddonDataContents>

                <AddonDataContents>
                  <FaMapMarkedAlt
                    size={22}
                    style={{ marginRight: "10px", color: "#C10E14" }}
                  />
                  Booming economy attracting global investors.
                </AddonDataContents>

                <AddonDataContents>
                  <FaLandmark
                    size={22}
                    style={{ marginRight: "10px", color: "#C10E14" }}
                  />
                  Iconic landmarks and strategic location.
                </AddonDataContents>

                <AddonDataContents>
                  <FaChartLine
                    size={22}
                    style={{ marginRight: "10px", color: "#C10E14" }}
                  />
                  Tax benefits and investor-friendly regulations.
                </AddonDataContents>

                <AddonDataContents>
                  <FaLock
                    size={22}
                    style={{ marginRight: "10px", color: "#C10E14" }}
                  />
                  Secure legal framework for real estate investments.
                </AddonDataContents>

                <AddonDataContents>
                  <FaGem
                    size={22}
                    style={{ marginRight: "10px", color: "#C10E14" }}
                  />
                  Ideal destination for both luxury and commercial properties.
                </AddonDataContents>
              </Box>
            </Grid>
            {/* Right Image Section */}
            <Grid
              item
              xs={12}
              md={6}
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                px: { xs: 2, sm: 4, md: 6 },
                py: { xs: 2, md: 4 },
                // mr:{lg:2},
              }}
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: "easeOut" }}
                viewport={{ once: true, amount: 0.5 }} // triggers when 50% visible, only once
              >
                <Box
                  component="img"
                  src={whydubai}
                  alt="whyDubai"
                  sx={{
                    width: { xs: "100%", md: "90%", lg: "100%" },
                    height: "auto",
                    maxHeight: {
                      xs: "auto",
                      md: "500px",
                      lg: "600px",
                      xl: "700px",
                    },
                    objectFit: "cover",
                    borderRadius: "8px",
                    boxShadow: "0 8px 16px rgba(0, 0, 0, 0.2)",
                    transition: "all 0.3s ease-in-out",
                  }}
                />
              </motion.div>
            </Grid>
          </Grid>
        </WhyDubaiSection>

        {/* latest offplan properties section         */}
        <div
          style={{
            marginTop: "80px",
            maxWidth: "1400px",
            marginLeft: "auto",
            marginRight: "auto",
          }}
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            viewport={{ once: true, amount: 0.5 }} // triggers when 50% visible, only once
          >
            <SectionHeader variant="h2">
              Latest Off Plan properties
            </SectionHeader>
          </motion.div>

          <Grid
            container
            spacing={2}
            justifyContent="center"
            sx={{
              mt: "20px",
              px: { xs: 2, md: 12 }, // Horizontal padding only for md and above
            }}
          >
            {latestProperties?.slice(0, 4).map((feature, index) => {
              const date = new Date(feature?.delivery_date * 1000); 
              // console.log("feature?.delivery_date",feature)
              // console.log("date",date)
              const formattedDate = date.toDateString(); // "Mon Jan 04 2038"
              // console.log("formattedDate",formattedDate)

              return (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={3}
                  key={index}
                  sx={{
                    maxWidth: "445px",

                    mx: "auto", // Horizontally center on small screens
                    px: { xs: 0, md: 1 }, // Padding only from md screens and above
                    py: 1,
                  }}
                >
                  <PropertyCard
                    key={index}
                    elevation={3}
                    onClick={() =>
                      navigate(ROUTES.PROJECT_DETAIL(feature?.api_property_id))
                    }
                  >
                    <ImageContainer>
                      {/* <PriceTag>{formattedDate}</PriceTag> */}
                      {/* <PropertyType
                    label={feature?.type}
                    // color={item.type === "Apartment" ? "primary" : "secondary"}
                    color="black"
                    size="small"
                  /> */}
                      <CardMedia
                        component="img"
                        image={feature?.cover_image}
                        alt="Property Images"
                        sx={{
                          width: "100%",
                          height: "100%",
                          backgroundColor: "white",
                          objectFit: "cover",
                          transition: "transform 0.5s",
                          "&:hover": {
                            transform: "scale(1.05)",
                          },
                        }}
                      />
                    </ImageContainer>

                    <CardContent sx={{ padding: "20px" }}>
                      <Typography
                        variant="subtitle1"
                        component="div"
                        sx={{
                          fontSize: "20px",
                          fontWeight: "600",
                          marginBottom: "12px",
                          lineHeight: 1.3,
                          minHeight: "42px",
                          height: "auto",
                          overflow: "hidden",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                        }}
                      >
                        {feature?.title}
                      </Typography>

                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          marginTop: "10px",
                          marginBottom: "15px",
                          gap: "8px",
                          color: "text.secondary",
                          fontSize: "14px",
                        }}
                      >
                        <FaCalendar style={{ color: "#C10E14" }} />
                        <Typography noWrap variant="body2">
                         Sales status : {feature?.sales_status}
                        </Typography>
                      </Box>
                    </CardContent>
                  </PropertyCard>
                </Grid>
              );
            })}
          </Grid>
        </div>

        {/* featured properties section         */}
        <div
          style={{
            marginTop: "80px",
            maxWidth: "1400px",
            marginLeft: "auto",
            marginRight: "auto",
          }}
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            viewport={{ once: true, amount: 0.5 }} // triggers when 50% visible, only once
          >
            <SectionHeader variant="h2">Featured Properties</SectionHeader>
          </motion.div>

          <Grid
            container
            spacing={2}
            justifyContent="center"
            sx={{
              mt: "20px",
              px: { xs: 2, md: 12 }, // Horizontal padding only for md and above
            }}
          >
            {featuredProperties.slice(0, 6).map((feature, index) => (
              <Grid
                item
                xs={12}
                sm={6}
                md={4}
                key={index}
                sx={{
                  maxWidth: "445px",

                  mx: "auto", // Horizontally center on small screens
                  px: { xs: 0, md: 1 }, // Padding only from md screens and above
                  py: 1,
                }}
              >
                <PropertyCard
                  key={index}
                  elevation={3}
                  onClick={() =>
                    navigate(ROUTES.FEATURED_PROPERTY_DETAIL(feature?.slug))
                  }
                >
                  <ImageContainer>
                    <PriceTag>{feature?.type}</PriceTag>
                    <CardMedia
                      component="img"
                      image={feature.thumbnail}
                      alt="Property Images"
                      sx={{
                        width: "100%",
                        height: "100%",
                        backgroundColor: "red",
                        objectFit: "cover",
                        transition: "transform 0.5s",
                        "&:hover": {
                          transform: "scale(1.05)",
                        },
                      }}
                    />
                  </ImageContainer>

                  <CardContent sx={{ padding: "20px" }}>
                    <Typography
                      variant="subtitle1"
                      component="div"
                      sx={{
                        fontSize: "20px",
                        fontWeight: "600",
                        // marginBottom: "12px",
                        lineHeight: 1.3,
                        height: "48px",
                        overflow: "hidden",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                      }}
                    >
                      {feature?.title}
                    </Typography>

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        marginTop: "10px",
                        marginBottom: "15px",
                        gap: "8px",
                        color: "text.secondary",
                        fontSize: "14px",
                      }}
                    >
                      <FaLocationDot style={{ color: "#C10E14" }} />
                      <Typography noWrap variant="body2">
                        {feature.location.length > 40
                          ? feature.location.slice(0, 40) + "..."
                          : feature.location}
                      </Typography>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 1,
                      }}
                    >
                      <ContactButton
                        variant="contained"
                        color="primary"
                        // startIcon={<MdEmail />}
                        fullWidth
                        sx={{ bgcolor: "#C10E14" }}
                        onClick={() =>
                          navigate(
                            ROUTES.FEATURED_PROPERTY_DETAIL(feature?.slug)
                          )
                        }
                      >
                        Start from {feature?.price} AED
                      </ContactButton>
                    </Box>
                  </CardContent>
                </PropertyCard>
              </Grid>
            ))}
          </Grid>
        </div>

        {/* freehold section */}
        <div
          style={{
            marginBottom: "80px",
            backgroundColor: "#f6f6f6",
          }}
        >
          <Grid
            container
            sx={{
              mt: { xs: 8, md: 5 },
              px: { xs: 2, sm: 4, md: 6, lg: 8 },
              py: { xs: 4, md: 4 },
              maxWidth: "1400px",
              mx: "auto",
            }}
          >
            {/* Right Image Section */}
            <Grid
              item
              xs={12}
              md={6}
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                px: { xs: 2, sm: 4, md: 6 },
                py: { xs: 4, md: 4 },
                // mr:{lg:2},
                // backgroundColor: "red",
              }}
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: "easeOut" }}
                viewport={{ once: true, amount: 0.5 }} // triggers when 50% visible, only once
              >
                <Box
                  component="img"
                  src={freehold}
                  alt="whyDubai"
                  sx={{
                    width: { xs: "100%", md: "90%", lg: "100%" },
                    height: { xs: "auto", md: "50%", lg: "auto" },
                    maxHeight: {
                      xs: "400px",
                      md: "500px",
                      lg: "600px",
                      xl: "700px",
                    },
                    objectFit: "cover",
                    borderRadius: "8px",
                    boxShadow: "0 8px 16px rgba(0, 0, 0, 0.2)",
                    transition: "all 0.3s ease-in-out",
                  }}
                />
              </motion.div>
            </Grid>
            {/* Left Text Section */}
            <Grid
              item
              xs={12}
              md={6}
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: { xs: "center", md: "flex-start" },
                py: { xs: 2, md: 4 },
                pl: { xs: 0, md: 4 },
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  maxWidth: "700px",
                  px: { xs: 2, md: 0 },
                }}
              >
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, ease: "easeOut" }}
                  viewport={{ once: true, amount: 0.5 }} // triggers when 50% visible, only once
                >
                  <AddonDataHead>Free Hold in Dubai</AddonDataHead>
                </motion.div>

                <AddonDataContents>
                  <FaMapMarkedAlt
                    size={22}
                    style={{ marginRight: "10px", color: "#C10E14" }}
                  />
                  Dubai offers Freehold property ownership for foreign nationals
                  in select areas.
                </AddonDataContents>

                <AddonDataContents>
                  <MdFamilyRestroom
                    size={22}
                    style={{ marginRight: "10px", color: "#C10E14" }}
                  />
                  Full ownership for life with the option to pass it on to your
                  family.
                </AddonDataContents>

                <AddonDataContents>
                  <MdOutlineWorkspacePremium
                    size={22}
                    style={{ marginRight: "10px", color: "#C10E14" }}
                  />
                  Ideal for both luxury homes and investment properties.
                </AddonDataContents>

                <AddonDataContents>
                  <MdSecurity
                    size={22}
                    style={{ marginRight: "10px", color: "#C10E14" }}
                  />
                  Freehold market ensures security and growth potential.
                </AddonDataContents>

                <AddonDataContents>
                  <MdSupportAgent
                    size={22}
                    style={{ marginRight: "10px", color: "#C10E14" }}
                  />
                  DESCO Real Estate's professionals guide first-time investors.
                </AddonDataContents>

                {/* <AddonDataContents>
                  <MdRealEstateAgent
                    size={22}
                    style={{ marginRight: "10px", color: "#C10E14" }}
                  />
                  Expert support to navigate the process and find the perfect
                  property.
                </AddonDataContents>

                <AddonDataContents>
                  <MdVerifiedUser
                    size={22}
                    style={{ marginRight: "10px", color: "#C10E14" }}
                  />
                  Your investment is in safe, capable hands.
                </AddonDataContents> */}
              </Box>
            </Grid>
          </Grid>
        </div>

        {/* top areas section         */}
        <Box
          sx={{
            my: "80px",
            mb: { xs: "80px", sm: "100px" },
            px: { xs: "20px", lg: "120px" },
          }}
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            viewport={{ once: true, amount: 0.5 }} // triggers when 50% visible, only once
          >
            <SectionHeader variant="h2">Top Areas</SectionHeader>
          </motion.div>
          <div style={{ marginTop: "40px" }}>
            <Grid
              container
              spacing={2}
              justifyContent="center"
              sx={{
                px: 2,
              }}
            >
              {topAreas?.data?.slice(0, 6).map((item, index) => (
                <Grid
                  item
                  xs={12} // 1 card below 600px (full width)
                  sm={6} // 2 cards from 600px to 1200px (half width)
                  lg={4} // 3 cards above 1200px (one-third width)
                  key={index}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    // backgroundColor: "rgba(103, 103, 103, 0.1)",
                    // padding: "8px",
                    borderRadius: "8px",
                    // height: "300px",
                    transition: "transform 0.3s ease-in-out",
                    "&:hover": {
                      transform: "scale(1.05)",
                    },
                  }}
                >
                  <Box
                    component="img"
                    src={item.thumbnail}
                    alt={item.title}
                    sx={{
                      width: { xs: "100%", sm: "100%", md: "95%" },
                      borderRadius: "8px",
                      objectFit: "cover",
                      height: { xs: "200px", sm: "200px", md: "300px" },
                      maxHeight: "300px",
                      cursor: "pointer", // good UX for clickable images
                    }}
                    onClick={() =>
                      window.open(ROUTES.AREA_DETAIL(item?.slug), "_blank")
                    }
                  />

                  <Typography
                    sx={{ mt: 2, fontWeight: 600, textAlign: "center" }}
                  >
                    {item.title}
                  </Typography>
                </Grid>
              ))}
            </Grid>
          </div>

          <Box
            sx={{
              mt: 5,
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Button
              // variant="contained"
              // color="red"
              sx={{
                fontWeight: 600,
                marginRight: { sm: "0px", md: "10px", lg: "15px" },
                px: 4,
                py: 1.5,
                fontSize: { xs: "14px", sm: "16px" },
                borderRadius: "30px",
                textTransform: "none",
                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                color: "#C10E14",
              }}
              onClick={() => navigate(ROUTES.AREAS)}
            >
              Explore More Areas
            </Button>
          </Box>
        </Box>

        {/* contact form section      */}
        <Box
          sx={{
            py: { xs: 6, md: 0 }, // Vertical padding: 10 * 8px = 80px
            backgroundImage: `url(${contactBg})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
          }}
        >
          <ContactSection />
        </Box>

        <Box
          style={{
            padding: { xs: "60px 60px ", sm: " 0px 0px " },
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
          }}
        >
          <NewsletterSignup />
        </Box>
        <Box
          style={{
            padding: "0px 0px ",
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundColor: "#f1c1c3",
            backgroundRepeat: "no-repeat",
          }}
        >
          <Links />
        </Box>

        {/* WhatsApp Floating Button */}
        <div
          style={{
            position: "fixed",
            bottom: "20px",
            right: "20px",
            zIndex: 1000,
          }}
        >
          <Tooltip title="Chat with us on WhatsApp!" arrow>
            <a
              href={whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: "none" }}
            >
              <Button
                variant="contained"
                color="success"
                sx={{
                  borderRadius: "50%",
                  minWidth: "60px",
                  height: "60px",
                  backgroundColor: "#25D366",
                  "&:hover": { backgroundColor: "#128C7E" },
                }}
              >
                <WhatsAppIcon sx={{ fontSize: "30px", color: "white" }} />
              </Button>
            </a>
          </Tooltip>
        </div>
      </Box>
    </>
  );
};

export default Home;
